import classNames from 'classnames';

import lang from '../../lib/language';
import { formatCurrencyBySymbol } from '../../lib/util/DataHelpers';
import { INVALID_VALUE } from '../../lib/util/types';

import NoDataIcon from '../../assets/img/icon-no-data.svg';
import CompanyLogo from '../../components/CompanyLogo/CompanyLogo';
import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';
import ClickableDiv from '../ClickableDiv/ClickableDiv';
import DiscoverButton from '../DiscoverButton/DiscoverButton';
import useIsMobile from '../hooks/useIsMobile';

import { formatEarningsCardDate, getEarningsSurpriseColorState, truncateText } from './helpers';
import { EarningRow, UpcomingEarningsProps } from './UpcomingEarnings.types';

import styles from './UpcomingEarnings.module.scss';

const NAME_TRUNCATE_SUFFIX = '..';
const NAME_TRUNCATE_CHARS = 35;
const LOADER_ROW_COUNT = 4;

const UpcomingEarnings = ({
  earnings,
  isLoading,
  onViewAllClick,
  onSymbolClick,
}: UpcomingEarningsProps) => {
  const { isMobile } = useIsMobile();

  const renderLoadingState = () => (
    <div className={styles.loaders}>
      {Array.from({ length: LOADER_ROW_COUNT }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className={styles.loadingRow} key={`earnings-loader-${index}`} />
      ))}
    </div>
  );

  const renderNoData = () => (
    <div className={styles.noDataWrapper}>
      <img className={styles.noDataIcon} src={NoDataIcon} alt='no data icon' />
      <p className={styles.noDataText}>{lang.upcomingEarningsNoData()}</p>
    </div>
  );

  const formatEarningsAmount = (value: string, currencySymbol: string): string => {
    if (!value) return INVALID_VALUE;

    return formatCurrencyBySymbol(value, currencySymbol);
  };

  const formatSignedEarningsValue = (value: string, currencySymbol: string): string => {
    const formattedValue = formatEarningsAmount(value, currencySymbol);

    return Number(value) > 0 ? `+${formattedValue}` : formattedValue;
  };

  const renderDateBlock = (date: string) => {
    const { isToday, day, month } = formatEarningsCardDate(date);

    if (isToday) {
      return <span className={styles.today}>{lang.commonDiscoverCalenderEventsToday()}</span>;
    }

    return (
      <div className={styles.dateWrapper}>
        <span className={styles.day}>{day}</span>
        <span className={styles.month}>{month}</span>
      </div>
    );
  };

  // HW-2199: reported values are coloured by the surprise (green beat, red miss, white bold met),
  // so the EPS drops its "+" on a miss rather than show a positive sign in red. Upcoming has no
  // actual to compare against, so the estimate is only signed, never coloured.
  const renderRightPanel = (item: EarningRow) => {
    if (item.isReported) {
      const surpriseColorState = getEarningsSurpriseColorState(item.epsSurprise);
      const surpriseColorClass = surpriseColorState && surpriseColorState !== 'neutral'
        ? styles[surpriseColorState]
        : undefined;
      const surpriseValue = formatSignedEarningsValue(item.epsSurprise, item.currencySymbol);
      const epsValue = surpriseColorState === 'negative'
        ? formatEarningsAmount(item.eps, item.currencySymbol)
        : formatSignedEarningsValue(item.eps, item.currencySymbol);

      if (isMobile) {
        return (
          <div className={classNames(styles.mobileStack, styles.dataColumn)}>
            <span className={styles.columnValue}>
              {lang.headerEarningsEPS()}{' '}
              <span className={classNames(styles.boldEps, surpriseColorClass)}>{epsValue}</span>
            </span>
            <span className={styles.columnValue}>
              {lang.headerEarningsSurprise()}{' '}
              <span className={classNames(styles.boldSurp, surpriseColorClass)}>{surpriseValue}</span>
            </span>
          </div>
        );
      }

      return (
        <>
          <div className={styles.dataColumn}>
            <p className={styles.headerTitle}>{lang.headerEarningsEPS()}</p>
            <span className={classNames(styles.columnValue, styles.boldEps, surpriseColorClass)}>{epsValue}</span>
          </div>
          <div className={classNames(styles.dataColumn, styles.trailingColumn)}>
            <p className={styles.headerTitle}>{lang.headerEarningsSurprise()}</p>
            <span className={classNames(styles.columnValue, styles.boldSurp, surpriseColorClass)}>{surpriseValue}</span>
          </div>
        </>
      );
    }

    const epsEstValue = formatSignedEarningsValue(item.epsEst, item.currencySymbol);
    const eventTimeValue = item.displayTime || lang.commonDiscoverCalenderSectionTimeNotSupplied();

    if (isMobile) {
      return (
        <div className={classNames(styles.mobileStack, styles.dataColumn)}>
          <span className={styles.columnValue}>
            {lang.headerEarningsCalendarEarningsEPSEstimateMobile()}{' '}
            <span className={styles.boldEpsEst}>{epsEstValue}</span>
          </span>
          <span className={styles.columnValue}>{eventTimeValue}</span>
        </div>
      );
    }

    return (
      <>
        <div className={classNames(styles.eventTime, styles.dataColumn)}>
          <p className={styles.headerTitle}>{lang.headerEarningsEventTime()}</p>
          <div className={styles.columnValue}>{eventTimeValue}</div>
        </div>
        <div className={classNames(styles.detailEps, styles.dataColumn, styles.trailingColumn)}>
          <p className={styles.headerTitle}>{lang.headerEarningsCalendarEarningsEPSEstimate()}</p>
          <span className={classNames(styles.columnValue, styles.boldEpsEst)}>{epsEstValue}</span>
        </div>
      </>
    );
  };

  const renderEarningsRows = () => (
    <div className={styles.listWrapper}>
      {earnings.map((item: EarningRow) => (
        <ClickableDiv
          key={item.id || `${item.symbol}-${item.date}-${item.time}`}
          className={classNames(styles.row, item.isReported ? styles.reported : styles.upcoming)}
          onClick={() => onSymbolClick(item.symbol)}
        >
          <div className={styles.detailDate}>
            {renderDateBlock(item.date)}
          </div>
          <div className={styles.innerData}>
            <div className={styles.companyWrapper}>
              {/* Rendered unconditionally: CompanyLogo falls back to the default
                  bubble itself when the symbol has no logo data at all. */}
              <CompanyLogo
                url={item.url}
                logo={item.logo}
                symbol={item.symbol}
              />
              <div className={styles.companyInfo}>
                <div className={styles.symbol}>{item.symbol}</div>
                {!isMobile && (
                <div className={styles.name}>
                  {truncateText(item.displayName, NAME_TRUNCATE_CHARS, NAME_TRUNCATE_SUFFIX) || INVALID_VALUE}
                </div>
              )}
              </div>
            </div>
            <div className={styles.timeAndPrice}>
              {renderRightPanel(item)}
            </div>
          </div>
        </ClickableDiv>
      ))}
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.textWrapper}>
          <div className={GlobalClassesEnum.CommonDiscoverTitle}>{lang.discoverEarningsCalendarTitle()}</div>
          {isMobile ? (
            <Button
              variant={ButtonTypesEnum.Link}
              onClick={onViewAllClick}
              className={styles.viewAll}
            >
              {lang.commonViewAll()}
            </Button>
          ) : (
            <DiscoverButton
              onClick={onViewAllClick}
              text={lang.commonViewAll()}
            />
          )}
        </div>
      </div>
      {isLoading && !earnings.length && renderLoadingState()}
      {!!earnings.length && renderEarningsRows()}
      {!isLoading && !earnings.length && renderNoData()}
    </div>
  );
};

export default UpcomingEarnings;
